<template>
    <div>
        <div v-if="isLoading" class="w-10 h-10 mt-20 ease-linear border-4 border-t-4 border-gray-200 border-top-rounded-full loader"></div>

        <div v-else-if="!hasData" class="px-4 py-10 text-sm text-center text-gray-500">
            No CMS utilization data is available for this surgeon yet.
        </div>

        <div v-else class="space-y-4">
            <div>
                <h4 class="text-lg font-bold text-gray-900">
                    Procedural Focus Trend
                </h4>
                <p class="text-sm text-gray-500">
                    Volume and procedural mix across the most recent reporting periods
                    <p class="text-xxs text-gray-400">
                    *Data is based CMS Utilization Data as available from data.cms.gov.
                    </p>
                </p>
            </div>

            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div class="overflow-hidden bg-white rounded-lg shadow">
                    <div class="px-4 py-5 sm:px-6">
                        <h5 class="text-lg font-bold">Services over time</h5>
                    </div>
                    <div class="px-2 pb-4 sm:px-4">
                        <apex-chart
                            type="area"
                            height="280"
                            width="100%"
                            :options="volumeChartOptions"
                            :series="volumeChartSeries"
                        ></apex-chart>
                    </div>
                </div>

                <div class="overflow-hidden bg-white rounded-lg shadow">
                    <div class="px-4 py-5 sm:px-6">
                        <h5 class="text-lg font-bold">Category mix</h5>
                        <p class="text-xs text-gray-500">Most recent period · rolled up to specialty root</p>
                    </div>
                    <div class="px-2 pb-4 sm:px-4">
                        <apex-chart
                            v-if="categoryChartSeries[0].data.length"
                            type="donut"
                            height="280"
                            width="100%"
                            :options="categoryChartOptions"
                            :series="categoryChartSeries[0].data"
                        ></apex-chart>
                        <p v-else class="py-16 text-sm text-center text-gray-500">
                            No categorized volume for the most recent period.
                        </p>
                    </div>
                </div>
            </div>

            <div class="overflow-hidden bg-white rounded-lg shadow">
                <div class="px-4 py-5 sm:px-6">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h5 class="text-lg font-bold">Top procedures</h5>
                            <p class="text-xs text-gray-500">
                                Top {{ dashboard.top_procedures_limit || 15 }} by volume across the full trend window
                            </p>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="section in procedureSections"
                                :key="section.key"
                                type="button"
                                class="px-3 py-1 text-sm font-medium rounded-md border"
                                :class="selectedProcedureGroup === section.key
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
                                @click="selectedProcedureGroup = section.key"
                            >
                                {{ section.label }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 tw-table">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">CPT</th>
                                <th class="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Description</th>
                                <th class="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Category</th>
                                <th class="px-4 py-2 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">Services</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(procedure, index) in filteredTopProcedures"
                                :key="procedure.code"
                                :class="{ 'bg-gray-50': index % 2 === 0 }"
                            >
                                <td class="px-4 py-2 text-sm font-medium text-gray-700">{{ procedure.code }}</td>
                                <td class="px-4 py-2 text-sm text-gray-700">{{ procedure.description || '—' }}</td>
                                <td class="px-4 py-2 text-sm text-gray-600">
                                    <span
                                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded cursor-help"
                                        :title="specialtyPathLabel(procedure.specialty_path)"
                                    >
                                        {{ specialtyLeafLabel(procedure) }}
                                    </span>
                                </td>
                                <td class="px-4 py-2 text-sm font-medium text-right text-gray-900">
                                    {{ formatNumber(procedure.total_services) }}
                                </td>
                            </tr>
                            <tr v-if="!filteredTopProcedures.length">
                                <td colspan="4" class="px-4 py-8 text-sm text-center text-gray-500">
                                    No {{ selectedProcedureSectionLabel.toLowerCase() }} in this window.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        searchRoute: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            selectedProcedureGroup: 'procedural',
            procedureSections: [
                { key: 'procedural', label: 'Procedural' },
                { key: 'diagnostic', label: 'Diagnostic' },
                { key: 'evaluation', label: 'Evaluation & visits' },
            ],
            dashboard: {
                years: [],
                latest_year: null,
                window_size: 5,
                totals_by_year: [],
                categories_by_year: [],
                top_procedures: {
                    procedural: [],
                    diagnostic: [],
                    evaluation: [],
                },
                top_procedures_limit: 15,
            },
        };
    },

    computed: {
        hasData() {
            return Array.isArray(this.dashboard.years) && this.dashboard.years.length > 0;
        },
        filteredTopProcedures() {
            const groups = this.dashboard.top_procedures || {};
            return groups[this.selectedProcedureGroup] || [];
        },
        selectedProcedureSectionLabel() {
            const section = this.procedureSections.find(
                (item) => item.key === this.selectedProcedureGroup
            );
            return section ? section.label : 'Procedural';
        },
        /**
         * Chronological Year 1..N labels (oldest = Year 1, newest = Year N).
         * Calendar years stay internal to the API payload only.
         */
        yearLabelsByCalendarYear() {
            const labels = {};
            (this.dashboard.years || []).forEach((year, index) => {
                labels[year] = 'Year ' + (index + 1);
            });
            return labels;
        },
        volumeChartSeries() {
            return [{
                name: 'Services',
                data: (this.dashboard.totals_by_year || []).map((row) => row.services),
            }];
        },
        volumeChartOptions() {
            const labels = (this.dashboard.totals_by_year || []).map(
                (row) => this.yearLabelsByCalendarYear[row.year] || ''
            );

            return {
                chart: {
                    toolbar: { show: false },
                },
                dataLabels: { enabled: false },
                stroke: {
                    curve: 'smooth',
                    width: 1.75,
                },
                fill: {
                    gradient: {
                        enabled: true,
                        opacityFrom: 0.55,
                        opacityTo: 0,
                    },
                },
                xaxis: {
                    categories: labels,
                },
                yaxis: {
                    labels: {
                        formatter: (value) => this.formatCompact(value),
                    },
                },
                grid: { show: false },
            };
        },
        categoryRowsForLatestPeriod() {
            return (this.dashboard.categories_by_year || []).filter(
                (row) => Number(row.year) === Number(this.dashboard.latest_year)
            );
        },
        categoryChartSeries() {
            return [{
                data: this.categoryRowsForLatestPeriod.map((row) => row.services),
            }];
        },
        categoryChartOptions() {
            return {
                labels: this.categoryRowsForLatestPeriod.map((row) => row.category),
                legend: {
                    position: 'bottom',
                },
                chart: {
                    toolbar: { show: false },
                },
                dataLabels: { enabled: false },
                tooltip: {
                    y: {
                        formatter: (value) => this.formatNumber(value) + ' services',
                    },
                },
            };
        },
    },

    created() {
        this.loadDashboard();
    },

    methods: {
        async loadDashboard() {
            try {
                this.isLoading = true;
                const response = await axios.get(this.searchRoute);
                this.dashboard = Object.assign({}, this.dashboard, response.data || {});
                this.selectedProcedureGroup = 'procedural';
            } catch (error) {
                this.dashboard = {
                    years: [],
                    latest_year: null,
                    window_size: 5,
                    totals_by_year: [],
                    categories_by_year: [],
                    top_procedures: {
                        procedural: [],
                        diagnostic: [],
                        evaluation: [],
                    },
                    top_procedures_limit: 15,
                };
                this.selectedProcedureGroup = 'procedural';
            } finally {
                this.isLoading = false;
            }
        },
        specialtyPathLabel(path) {
            if (!Array.isArray(path) || !path.length) {
                return '—';
            }
            return path.join(' > ');
        },
        specialtyLeafLabel(procedure) {
            if (procedure.specialty) {
                return procedure.specialty;
            }

            const path = procedure.specialty_path;
            if (Array.isArray(path) && path.length) {
                return path[path.length - 1];
            }

            return procedure.specialty_root || 'Uncategorized';
        },
        formatNumber(value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return Intl.NumberFormat('en-US', {
                maximumFractionDigits: 1,
            }).format(value);
        },
        formatCompact(value) {
            const val = Math.abs(Number(value) || 0);
            if (val >= 1000000) {
                return (val / 1000000).toFixed(1) + 'M';
            }
            if (val >= 1000) {
                return (val / 1000).toFixed(1) + 'K';
            }
            return String(Math.round(val));
        },
    },
};
</script>
